/**
 * The Facilities page's figures (review #226, Shayna 2026-10-08), off the bundle's
 * `facilityMonths`: employee × home department × facility group × facility × month ×
 * claim cell (claim type, service type, status — so the claim filters select rows).
 *
 * Her green metrics, and only those: cost per claim, billed claims, collection rate,
 * claims billed vs resolved %, 0% touch share and touches per touched claim. Every
 * one is POOLED — sums over the rows, then one division (MEASURE-RULES §3.1) — never a
 * mean of facility or person rates:
 *
 *   cost per claim          Σ labor ÷ Σ billed claims        (bill month)
 *   billed vs resolved %    Σ resolved ÷ Σ billed claims     (resolution month ÷ bill month; uncapped, §4.2)
 *   0% touch share          Σ no-touch ÷ Σ resolved buckets  (§5.1: a resolved denominator)
 *   touches per touched     Σ touches ÷ Σ touched            (§5.5)
 *   collection rate         Σ resolved charge ÷ Σ charge     over the range's lagged window (§4.3)
 *
 * Labor here is ALLOCATED claim labor — the crosstab's, which the Overview's facility
 * bars divide — not a person's whole payroll: payroll has no facility. Labor on no
 * claim stays with the facility it is filed under (UNKNOWN, mostly), as on the bars.
 *
 * Scoping is the API's (scope.ts): a scoped caller's table holds only their people's
 * rows, so every figure here is those people's contribution to the facility.
 */
import type { FacilityMonthsDict } from '@/data/types'
import { divide } from './core'
import { collectionWindow } from './collection'
import { sameValue } from '@/data/blankValue'

/** The summable measures, in the row's order after its five keys. */
export interface FacilityCell {
  claims: number
  labor: number
  resolved: number
  noTouch: number
  touched: number
  touches: number
  charge: number
  chargeResolved: number
}

const zero = (): FacilityCell => ({
  claims: 0,
  labor: 0,
  resolved: 0,
  noTouch: 0,
  touched: 0,
  touches: 0,
  charge: 0,
  chargeResolved: 0,
})

/** A row decoded: its names, and what it adds. */
export interface FacilityRow {
  employee: string
  department: string
  group: string
  facility: string
  month: string
  /** Its claim cell (`FacilityMonthsDict.cells`); null on a table not split by cell. */
  cell: number | null
}

/** A row's measures, scaled. Counts by the claim weight, labor by the labor weight. */
export interface RowWeight {
  claims: number
  labor: number
}

/** A person × department × facility, as the Weighted switch's factors are keyed. */
export const facilityCellKey = (e: string, d: string, f: string) => `${e}\u001f${d}\u001f${f}`

/**
 * How much of a facility row the page counts, or null to leave it out:
 *
 *  - a person not in `people` (the filter bar's scope): out;
 *  - with `sel` (the claim filters, on a table split by cell): a row in a cell they do
 *    not pick is out, and the rest count whole — exact;
 *  - without it: the person's `claimShare` — 1 and 1 with no claim filter, their share
 *    of the filtered claims under one (modelled);
 *  - `weighted` (the Weighted switch: weighted ÷ standard claims per person ×
 *    department × facility) scales the counts, never the labor.
 */
export function facilityRowWeight(opts: {
  people: readonly string[]
  claimShare: (name: string) => RowWeight
  sel: readonly boolean[] | null
  weighted: ReadonlyMap<string, number> | null
}): (row: FacilityRow) => RowWeight | null {
  const { sel, weighted } = opts
  const whole = { claims: 1, labor: 1 }
  const people = new Map(opts.people.map((e) => [e, sel ? whole : opts.claimShare(e)]))
  return (row) => {
    if (sel && (row.cell === null || !sel[row.cell])) return null
    const w = people.get(row.employee)
    if (!w) return null
    const f = weighted?.get(facilityCellKey(row.employee, row.department, row.facility)) ?? 1
    return f === 1 ? w : { claims: w.claims * f, labor: w.labor }
  }
}

/**
 * The filter bar's Facility group filter on this table: a row outside the groups is
 * out, the rest weigh as `weightOf` says. By the row's own group, so exact; blank
 * spellings are one "Unknown". `weightOf` itself when the filter is off.
 */
export function inFacilityGroups(
  weightOf: (row: FacilityRow) => RowWeight | null,
  groups: readonly string[],
): (row: FacilityRow) => RowWeight | null {
  if (groups.length === 0) return weightOf
  return (row) => (groups.some((g) => sameValue(row.group, g)) ? weightOf(row) : null)
}

/** Cells by month, and who worked them in which months. */
export interface FacilityBucket {
  byMonth: Map<string, FacilityCell>
  /** Person → the months they have claims or labor here. */
  people: Map<string, Set<string>>
}

/**
 * Every row of `fm` that `weightOf` keeps (null leaves the row out: a person not in
 * scope), grouped by `keyOf` (null leaves it out too). One bucket per key; a key may
 * be a facility, a group, a department, or one bucket for everything.
 */
export function bucketFacilityMonths(
  fm: FacilityMonthsDict,
  weightOf: (row: FacilityRow) => RowWeight | null,
  keyOf: (row: FacilityRow) => string | null,
): Map<string, FacilityBucket> {
  const out = new Map<string, FacilityBucket>()
  for (const r of fm.rows) {
    const row: FacilityRow = {
      employee: fm.employees[r[0]] ?? '',
      department: fm.departments[r[1]] ?? 'Unknown',
      group: fm.facilityGroups[r[2]] ?? 'Unknown',
      facility: fm.facilities[r[3]] ?? 'Unknown',
      month: fm.months[r[4]] ?? '',
      cell: r[13] ?? null,
    }
    const w = weightOf(row)
    if (!w) continue
    const key = keyOf(row)
    if (key === null) continue
    let b = out.get(key)
    if (!b) out.set(key, (b = { byMonth: new Map(), people: new Map() }))
    let c = b.byMonth.get(row.month)
    if (!c) b.byMonth.set(row.month, (c = zero()))
    c.claims += r[5] * w.claims
    c.labor += r[6] * w.labor
    c.resolved += r[7] * w.claims
    c.noTouch += r[8] * w.claims
    c.touched += r[9] * w.claims
    c.touches += r[10] * w.claims
    c.charge += r[11] * w.claims
    c.chargeResolved += r[12] * w.claims
    if (r[5] !== 0 || r[6] !== 0) {
      let ms = b.people.get(row.employee)
      if (!ms) b.people.set(row.employee, (ms = new Set()))
      ms.add(row.month)
    }
  }
  return out
}

/** The six green figures, and the sums under them. */
export interface FacilityFigures {
  claims: number
  labor: number
  resolved: number
  /** The resolved claims the touch buckets partition (§5.1). */
  touchBase: number
  touched: number
  touches: number
  charge: number
  chargeResolved: number
  /** People with claims or labor here in the months. */
  people: number
  costPerClaim: number | null
  billedVsResolved: number | null
  zeroTouch: number | null
  touchesPerTouched: number | null
  /** Null when the window has no charge: no basis, which is not 0%. */
  collection: number | null
}

/**
 * A bucket over the months [first … last of `months`]. Claims, labor, resolved and
 * touches add up the months themselves; collection reads the range's own window of
 * bill months (start − 8 … end − 3), as every collection figure does — so one month's
 * figure is that month's window, never a share of the range's.
 */
export function figuresOver(b: FacilityBucket | undefined, months: readonly string[]): FacilityFigures {
  const t = zero()
  let people = 0
  if (b && months.length) {
    const inRange = new Set(months)
    for (const [m, c] of b.byMonth) {
      if (!inRange.has(m)) continue
      t.claims += c.claims
      t.labor += c.labor
      t.resolved += c.resolved
      t.noTouch += c.noTouch
      t.touched += c.touched
      t.touches += c.touches
    }
    const sorted = [...months].sort()
    const win = collectionWindow(sorted[0]!, sorted[sorted.length - 1]!)
    for (const [m, c] of b.byMonth) {
      if (m < win.from || m > win.to) continue
      t.charge += c.charge
      t.chargeResolved += c.chargeResolved
    }
    for (const ms of b.people.values()) if ([...ms].some((m) => inRange.has(m))) people++
  }
  const touchBase = t.noTouch + t.touched
  return {
    claims: t.claims,
    labor: t.labor,
    resolved: t.resolved,
    touchBase,
    touched: t.touched,
    touches: t.touches,
    charge: t.charge,
    chargeResolved: t.chargeResolved,
    people,
    costPerClaim: divide(t.labor, t.claims),
    billedVsResolved: divide(t.resolved, t.claims),
    zeroTouch: divide(t.noTouch, touchBase),
    touchesPerTouched: divide(t.touches, t.touched),
    collection: t.charge > 0 ? t.chargeResolved / t.charge : null,
  }
}

/** The metric set, in Shayna's order. */
export type FacilityMetricKey =
  | 'costPerClaim'
  | 'claims'
  | 'collection'
  | 'billedVsResolved'
  | 'zeroTouch'
  | 'touchesPerTouched'

/**
 * Each facility's group and state: the one most of its claims sit in, as Claim detail
 * files a facility under a group. A name filed under two groups is one facility here.
 */
export function homeOf(
  rows: readonly { facility: string; key: string; claims: number }[],
): Map<string, string> {
  const tally = new Map<string, Map<string, number>>()
  for (const r of rows) {
    let m = tally.get(r.facility)
    if (!m) tally.set(r.facility, (m = new Map()))
    m.set(r.key, (m.get(r.key) ?? 0) + r.claims)
  }
  const out = new Map<string, string>()
  for (const [f, m] of tally) out.set(f, [...m.entries()].sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1))[0]![0])
  return out
}

/** Several buckets as one — a pooled rollup, people counted once. */
export function mergeBuckets(buckets: Iterable<FacilityBucket>): FacilityBucket {
  const out: FacilityBucket = { byMonth: new Map(), people: new Map() }
  for (const b of buckets) {
    for (const [m, c] of b.byMonth) {
      let t = out.byMonth.get(m)
      if (!t) out.byMonth.set(m, (t = zero()))
      t.claims += c.claims
      t.labor += c.labor
      t.resolved += c.resolved
      t.noTouch += c.noTouch
      t.touched += c.touched
      t.touches += c.touches
      t.charge += c.charge
      t.chargeResolved += c.chargeResolved
    }
    for (const [e, ms] of b.people) {
      let s = out.people.get(e)
      if (!s) out.people.set(e, (s = new Set()))
      for (const m of ms) s.add(m)
    }
  }
  return out
}

/** Who has claims or labor in the bucket within the months. */
export const peopleIn = (b: FacilityBucket | undefined, months: readonly string[]): string[] => {
  if (!b) return []
  const inRange = new Set(months)
  return [...b.people].filter(([, ms]) => [...ms].some((m) => inRange.has(m))).map(([e]) => e)
}

/** A facility on the person page: their claims there, and the two shares (review of #164). */
export interface PersonFacilityShare {
  facility: string
  /** The person's billed claims at the facility in the months. */
  claims: number
  /** Everyone's billed claims at the facility in the months: everyone in `fm`. */
  facilityClaims: number
  /** "% of their claims": this facility's share of the person's claims in the range. */
  ofTheirs: number
  /** "% of facility's claims": the person's share of the facility's claims; null with no basis. */
  ofFacility: number | null
}

/**
 * The person's billed claims by facility, each as a share of their own claims and of
 * the facility's — SUM ÷ SUM over the months, never a mean of monthly shares (§3.1).
 *
 * The facility's total is every person IN `fm`: the API scopes the table, so for a
 * scoped caller it is the claims billed by the people in their grant, never FCC-wide
 * (docs/ACCESS.md). `weightFor` carries the claim filters, the Facility group filter and
 * the Weighted switch, alike for the person and the total, so the two shares read the
 * same rows. Facilities where the person has no claims in the months are left out.
 */
export function personFacilityShares(
  fm: FacilityMonthsDict,
  weightFor: (people: readonly string[]) => (row: FacilityRow) => RowWeight | null,
  person: string,
  months: readonly string[],
): PersonFacilityShare[] {
  const mine = bucketFacilityMonths(fm, weightFor([person]), (r) => r.facility)
  const theirs = new Map<string, number>()
  for (const [f, b] of mine) {
    const n = figuresOver(b, months).claims
    if (n !== 0) theirs.set(f, n)
  }
  if (theirs.size === 0) return []
  const all = bucketFacilityMonths(fm, weightFor(fm.employees), (r) => (theirs.has(r.facility) ? r.facility : null))
  const total = [...theirs.values()].reduce((a, n) => a + n, 0)
  return [...theirs.entries()]
    .map(([facility, claims]) => {
      const facilityClaims = figuresOver(all.get(facility), months).claims
      return {
        facility,
        claims,
        facilityClaims,
        ofTheirs: total === 0 ? 0 : claims / total,
        ofFacility: divide(claims, facilityClaims),
      }
    })
    .sort((a, b) => b.claims - a.claims)
}
